//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { getLocale } from '@shared/conversions'

export function getLocaleSeparators(): { decimal: string; group: string } {
  const locale = getLocale()
  const formatter = new Intl.NumberFormat(locale)
  const parts = formatter.formatToParts(12345.6)

  const decimal = parts.find((p) => p.type === 'decimal')?.value || '.'
  const _group = parts.find((p) => p.type === 'group')?.value || ','

  return { decimal, group: _group }
}

export function parseLocaleNumber(value: string): number {
  if (!value || value.trim() === '') {
    return 0
  }

  const { decimal } = getLocaleSeparators()

  const commaCount = (value.match(/,/g) || []).length
  const periodCount = (value.match(/\./g) || []).length

  const looksLikeCommaDecimal =
    commaCount === 1 &&
    periodCount === 0 &&
    value.indexOf(',') > 0 &&
    value.substring(value.indexOf(',') + 1).length <= 3 &&
    /^\d+,\d{1,3}$/.test(value)

  if (decimal === ',' || looksLikeCommaDecimal) {
    if (commaCount === 1 && periodCount === 0) {
      const normalizedValue = value.replace(',', '.')
      return parseFloat(normalizedValue)
    }

    if (commaCount > 1) {
      const lastCommaIndex = value.lastIndexOf(',')
      const beforeLastComma = value.substring(0, lastCommaIndex)
      const afterLastComma = value.substring(lastCommaIndex + 1)

      if (afterLastComma.length <= 2 && /^\d+$/.test(afterLastComma)) {
        const integerPart = beforeLastComma.replace(/[,.]/g, '')
        return parseFloat(integerPart + '.' + afterLastComma)
      }
    }

    if (periodCount > 0 && commaCount > 0) {
      const lastCommaIndex = value.lastIndexOf(',')
      const beforeLastComma = value.substring(0, lastCommaIndex)
      const afterLastComma = value.substring(lastCommaIndex + 1)

      if (afterLastComma.length <= 2 && /^\d+$/.test(afterLastComma)) {
        const integerPart = beforeLastComma.replace(/\./g, '')
        return parseFloat(integerPart + '.' + afterLastComma)
      }
    }

    const normalizedValue = value.replace(/\./g, '').replace(',', '.')
    return parseFloat(normalizedValue)
  } else {
    const normalizedValue = value.replace(/,/g, '')
    return parseFloat(normalizedValue)
  }
}

export function isValidLocaleNumber(value: string): boolean {
  if (!value || value.trim() === '') {
    return false
  }

  try {
    const number = parseLocaleNumber(value)
    return !isNaN(number) && number >= 0
  } catch {
    return false
  }
}
